import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../shared/api/client";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../shared/ui";
import type { EstimateAccuracyReport, EstimateRow } from "../../../shared/types/api";
import { Figure, percent } from "./reportBits";
import { FilterBar, defaultFilters, toQuery, type ReportFilterValues } from "./reportFilters";

/**
 * Who overran their estimate: logged time against the original estimate, worst overrun first.
 *
 * <p>Only items that were estimated are counted — an overrun percentage over no estimate is
 * unanswerable, so unestimated work is left out rather than counted as accurate. The percentage
 * on a row is the aggregate over that person's overrunning items, not an average of averages.
 */
export function EstimateAccuracy() {
  const [filters, setFilters] = useState<ReportFilterValues>(defaultFilters);

  const report = useQuery({
    queryKey: ["report", "estimate-accuracy", filters],
    queryFn: () =>
      api.get<EstimateAccuracyReport>(`/api/v1/reports/estimate-accuracy?${toQuery(filters)}`),
  });

  return (
    <div className="space-y-4">
      <FilterBar values={filters} onChange={setFilters} />

      {report.isPending ? (
        <LoadingState label="Comparing time to estimate" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <Report data={report.data} />
      )}
    </div>
  );
}

function Report({ data }: { data: EstimateAccuracyReport }) {
  const overall =
    data.totalEstimatedHours > 0
      ? ((data.totalActualHours - data.totalEstimatedHours) / data.totalEstimatedHours) * 100
      : null;

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="People over" value={String(data.peopleCount)} note="ran past an estimate" />
        <Figure
          label="Items over"
          value={String(data.overrunItemCount)}
          note="logged beyond estimate"
          tone={data.overrunItemCount > 0 ? "amber" : undefined}
        />
        <Figure
          label="Estimated"
          value={`${data.totalEstimatedHours}h`}
          note="on overrunning items"
        />
        <Figure
          label="Overrun"
          value={percent(overall)}
          note={`${data.totalActualHours}h actually logged`}
          tone={overall != null && overall > 0 ? "red" : undefined}
        />
      </div>

      <Card>
        <CardHeader title="By person" subtitle="Biggest overrun first." />
        <CardBody className="p-0">
          {data.people.length === 0 ? (
            <EmptyState
              title="Everyone was within estimate"
              message="No estimated item ran over in this window and filter."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {data.people.map((row) => (
                <PersonRow key={row.employeeId} row={row} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function PersonRow({ row }: { row: EstimateRow }) {
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm text-ink-900">
          {row.employeeName}
          {row.department && <span className="ml-2 text-xs text-ink-500">{row.department}</span>}
        </span>
        <span className="flex items-center gap-3 text-xs">
          <span className="text-ink-500">
            {row.overrunCount} of {row.measuredCount} estimated
          </span>
          <span className="tnum text-ink-600">
            {row.estimatedHours}h → {row.actualHours}h
          </span>
          <span className="tnum w-12 text-right font-medium text-red-700">
            {percent(row.overrunPercent)}
          </span>
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        {row.items.map((item) => (
          <li key={item.workItemId} className="flex flex-wrap items-baseline gap-2 text-xs">
            <Link
              to={`/work-items/${item.itemKey}`}
              className="font-mono text-ink-500 hover:text-brand-600"
            >
              {item.itemKey}
            </Link>
            <span className="text-ink-700">{item.title}</span>
            <span className="tnum text-ink-500">
              est {item.estimatedHours}h · logged {item.actualHours}h
            </span>
            <span className="tnum text-red-700">{percent(item.overrunPercent)}</span>
          </li>
        ))}
      </ul>
    </li>
  );
}
